'use client';

import Icon from '@ant-design/icons';
import { Button, Input, Modal, message } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import RightSvg from '@/components/Icon/svgs/right.svg';
import { useTranslation } from '@/i18n/client';
import { type ICircleSettingDetail, updateCircleSetting } from '@/service/friend';

import styles from './index.less';

// 阻止事件冒泡到父组件
const stopPropagationModalRender = (node: ReactNode) => (
  <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
    {node}
  </div>
);

export interface IEditNameModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  circleRoute: string;
  initialName?: string;
  currentDetail?: ICircleSettingDetail | null;
  onSuccess?: (detail: ICircleSettingDetail) => void;
}

export const EditNickNameModal = (props: IEditNameModalProps): JSX.Element => {
  const { open, setOpen, circleRoute, initialName = '', currentDetail, onSuccess } = props;
  const { t } = useTranslation();

  const [editName, setEditName] = useState(initialName);
  const [saveLoading, setSaveLoading] = useState(false);

  // 当弹窗打开时，初始化名称
  useEffect(() => {
    if (open) {
      setEditName(initialName);
    }
  }, [open, initialName]);

  // 关闭弹窗
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // 保存群名称
  const handleSaveName = useCallback(() => {
    if (!editName.trim()) {
      message.warning(t('circle.nameRequired'));

      return;
    }

    setSaveLoading(true);
    updateCircleSetting({
      route: circleRoute,
      nickname: editName.trim()
    })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || t('error.oops'));
        }

        message.success(t('circle.updateSuccess'));
        handleClose();

        if (currentDetail) {
          onSuccess?.({
            ...currentDetail,
            nickname: editName.trim()
          });
        }
      })
      .catch((e) => {
        message.error(e.message);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  }, [circleRoute, currentDetail, editName, handleClose, onSuccess, t]);

  return (
    <Modal
      centered
      className={styles.editNameModal}
      closable={false}
      destroyOnClose
      footer={null}
      maskClosable
      modalRender={stopPropagationModalRender}
      onCancel={handleClose}
      open={open}
      width={580}
      zIndex={1100}
    >
      <div className={styles.editFormContainer}>
        {/* 头部 */}
        <div className={styles.editFormHeader}>
          <div className={styles.backBtn} onClick={handleClose}>
            <Icon className={styles.backIcon} component={RightSvg} />
          </div>
          <span className={styles.title}>{t('circle.editNickname')}</span>
          <div className={styles.placeholder} />
        </div>

        {/* 内容区 */}
        <div className={styles.editFormBody}>
          <div className={styles.inputCard}>
            <Input
              allowClear
              className={styles.editNameInput}
              maxLength={16}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={t('circle.editNickname')}
              showCount
              value={editName}
            />
          </div>

          {/* 保存按钮 */}
          <Button
            className={styles.saveBtn}
            disabled={saveLoading || !editName.trim()}
            loading={saveLoading}
            onClick={handleSaveName}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditNickNameModal;
